import Analytics from '@ublend-npm/aula-analytics';
import {
  getAssignmentsBySpaceId,
  getAssignmentById,
  getGradesByAssignmentId,
  postAssignment,
  postReleaseGrades,
  patchAssignment,
  deleteAssignment as deleteAssignmentEffect,
  postGrade,
  postGrades,
} from './assignments.effects';
import { selectors as coreSelectors, actions as coreActions } from '$data/core';
import { getAssignmentsIds } from './assignments.selectors';
import hasGrade from '$utils/hasGrade';

import {
  FETCH_ASSIGNMENTS_STARTED,
  FETCH_ASSIGNMENTS_SUCCESS,
  FETCH_ASSIGNMENTS_FAILED,
  FETCH_ASSIGNMENT_STARTED,
  FETCH_ASSIGNMENT_SUCCESS,
  FETCH_ASSIGNMENT_FAILED,
  FETCH_GRADES_STARTED,
  FETCH_GRADES_SUCCESS,
  FETCH_GRADES_FAILED,
  FETCH_STUDENT_GRADES_STARTED,
  FETCH_STUDENT_GRADES_SUCCESS,
  FETCH_STUDENT_GRADES_FAILED,
  UPDATE_ASSIGNMENT_GRADES_STARTED,
  UPDATE_ASSIGNMENT_GRADES_SUCCESS,
  UPDATE_ASSIGNMENT_GRADES_FAILED,
  CREATE_ASSIGNMENT_STARTED,
  CREATE_ASSIGNMENT_SUCCESS,
  CREATE_ASSIGNMENT_FAILED,
  RELEASE_GRADES_STARTED,
  RELEASE_GRADES_SUCCESS,
  RELEASE_GRADES_FAILED,
  GRADE_MANUAL_ASSIGNMENT_STARTED,
  GRADE_MANUAL_ASSIGNMENT_SUCCESS,
  GRADE_MANUAL_ASSIGNMENT_FAILED,
  UPDATE_ASSIGNMENT_STARTED,
  UPDATE_ASSIGNMENT_SUCCESS,
  UPDATE_ASSIGNMENT_FAILED,
  DELETE_ASSIGNMENT_SUCCESS,
  DELETE_ASSIGNMENT_FAILED,
  IMPORT_MANUAL_ASSIGNMENT_GRADES_STARTED,
  IMPORT_MANUAL_ASSIGNMENT_GRADES_SUCCESS,
  IMPORT_MANUAL_ASSIGNMENT_GRADES_FAILED,
} from './assignments.types';
import {
  FETCH_ASSIGNMENTS,
  FETCH_GRADES_FOR_ASSIGNMENT,
  CREATE_ASSIGNMENT,
  RELEASE_GRADES,
  EDIT_ASSIGNMENT,
  DELETE_ASSIGNMENT,
  GRADE_MANUAL_ASSIGNMENT,
} from '$variables/analyticsEvents';

// fetch all assignments
const fetchAssignmentsStarted = (payload) => ({
  type: FETCH_ASSIGNMENTS_STARTED,
  ...payload,
});

const fetchAssignmentsSuccess = (payload) => ({
  type: FETCH_ASSIGNMENTS_SUCCESS,
  ...payload,
});

const fetchAssignmentsError = (payload) => ({
  type: FETCH_ASSIGNMENTS_FAILED,
  ...payload,
});

// fetch single assignment
const fetchAssignmentStarted = (payload) => ({
  type: FETCH_ASSIGNMENT_STARTED,
  ...payload,
});

const fetchAssignmentSuccess = (payload) => ({
  type: FETCH_ASSIGNMENT_SUCCESS,
  ...payload,
});

const fetchAssignmentError = (payload) => ({
  type: FETCH_ASSIGNMENT_FAILED,
  ...payload,
});

// fetch grades for single assignment
const fetchGradesStarted = (payload) => ({
  type: FETCH_GRADES_STARTED,
  ...payload,
});

const fetchGradesSuccess = (payload) => ({
  type: FETCH_GRADES_SUCCESS,
  ...payload,
});

const fetchGradesError = (payload) => ({
  type: FETCH_GRADES_FAILED,
  ...payload,
});

// update grades for single assignment
const updateAssignmentGradesStarted = (payload) => ({
  type: UPDATE_ASSIGNMENT_GRADES_STARTED,
  ...payload,
});

const updateAssignmentGradesSuccess = (payload) => ({
  type: UPDATE_ASSIGNMENT_GRADES_SUCCESS,
  ...payload,
});

const updateAssignmentGradesError = (payload) => ({
  type: UPDATE_ASSIGNMENT_GRADES_FAILED,
  ...payload,
});

// fetch grades for one student
const fetchGradesForStudentStarted = (payload) => ({
  type: FETCH_STUDENT_GRADES_STARTED,
  ...payload,
});

const fetchGradesForStudentSuccess = (payload) => ({
  type: FETCH_STUDENT_GRADES_SUCCESS,
  ...payload,
});

const fetchGradesForStudentError = (payload) => ({
  type: FETCH_STUDENT_GRADES_FAILED,
  ...payload,
});

// create assignment
const createAssignmentStarted = (payload) => ({
  type: CREATE_ASSIGNMENT_STARTED,
  ...payload,
});

const createAssignmentSuccess = (payload) => ({
  type: CREATE_ASSIGNMENT_SUCCESS,
  ...payload,
});

const createAssignmentError = (payload) => ({
  type: CREATE_ASSIGNMENT_FAILED,
  ...payload,
});

// release grades
const releaseGradesStarted = (payload) => ({
  type: RELEASE_GRADES_STARTED,
  ...payload,
});

const releaseGradesSuccess = (payload) => ({
  type: RELEASE_GRADES_SUCCESS,
  ...payload,
});

const releaseGradesError = (payload) => ({
  type: RELEASE_GRADES_FAILED,
  ...payload,
});

// update assignment
const updateAssignmentStarted = (payload) => ({
  type: UPDATE_ASSIGNMENT_STARTED,
  ...payload,
});

const updateAssignmentSuccess = (payload) => ({
  type: UPDATE_ASSIGNMENT_SUCCESS,
  ...payload,
});

const updateAssignmentError = (payload) => ({
  type: UPDATE_ASSIGNMENT_FAILED,
  ...payload,
});

// delete assignment
const deleteAssignmentSuccess = (payload) => ({
  type: DELETE_ASSIGNMENT_SUCCESS,
  ...payload,
});

const deleteAssignmentError = (payload) => ({
  type: DELETE_ASSIGNMENT_FAILED,
  ...payload,
});

// grade manual assignment
const gradeManualAssignmentStarted = (payload) => ({
  type: GRADE_MANUAL_ASSIGNMENT_STARTED,
  ...payload,
});

const gradeManualAssignmentSuccess = (payload) => ({
  type: GRADE_MANUAL_ASSIGNMENT_SUCCESS,
  ...payload,
});

const gradeManualAssignmentError = (payload) => ({
  type: GRADE_MANUAL_ASSIGNMENT_FAILED,
  ...payload,
});

// import csv grades for manual assignment
const importManualAssignmentGradesStarted = (payload) => ({
  type: IMPORT_MANUAL_ASSIGNMENT_GRADES_STARTED,
  ...payload,
});

const importManualAssignmentGradesSuccess = (payload) => ({
  type: IMPORT_MANUAL_ASSIGNMENT_GRADES_SUCCESS,
  ...payload,
});

const importManualAssignmentGradesError = (payload) => ({
  type: IMPORT_MANUAL_ASSIGNMENT_GRADES_FAILED,
  ...payload,
});

export const fetchAssignments =
  ({ spaceId }) =>
  async (dispatch, getState) => {
    const payload = { spaceId };

    dispatch(fetchAssignmentsStarted(payload));

    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);

    try {
      const { assignments } = await getAssignmentsBySpaceId({
        spaceId,
        accessToken,
      });

      const isStudent = !coreSelectors.getIsEducator(getState());
      let assignmentsIncludingParents = assignments;

      // When getting the assignments for a given student, only the extension
      // assignments will be returned by the getAssignments endpoint. We need
      // to then get the parent assignments.
      if (isStudent) {
        const extensions = assignments.filter(
          ({ parentAssignmentId }) => !!parentAssignmentId
        );
        const parentAssignments = await Promise.all(
          extensions.map(async (ext) => {
            const { parentAssignmentId } = ext;
            const res = await getAssignmentById({
              assignmentId: parentAssignmentId,
              accessToken,
            });
            return res.assignment;
          })
        );

        const liveParentAssignments = parentAssignments.filter(
          (assignment) => assignment.status !== 'trash'
        );
        assignmentsIncludingParents = [
          ...assignments,
          ...liveParentAssignments,
        ];
      }

      dispatch(
        fetchAssignmentsSuccess({
          ...payload,
          assignments: assignmentsIncludingParents,
        })
      );
      Analytics.track({
        event: FETCH_ASSIGNMENTS,
        totalAssignments: assignments.length,
      });
    } catch (error) {
      dispatch(fetchAssignmentsError({ ...payload, error }));
    }
  };

export const fetchAssignment =
  ({ assignmentId }) =>
  async (dispatch, getState) => {
    const payload = { assignmentId };

    dispatch(fetchAssignmentStarted(payload));

    const spaceId = coreSelectors.getCurrentSpaceId(getState());
    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);

    try {
      const { assignment } = await getAssignmentById({
        accessToken,
        assignmentId,
      });
      dispatch(fetchAssignmentSuccess({ ...payload, assignment }));
    } catch (error) {
      dispatch(fetchAssignmentError({ ...payload, error }));
    }
  };

export const fetchGradesForAssignment =
  ({ assignmentId }) =>
  async (dispatch, getState) => {
    const payload = { assignmentId };

    dispatch(fetchGradesStarted(payload));

    const spaceId = coreSelectors.getCurrentSpaceId(getState());
    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);

    try {
      const { grades } = await getGradesByAssignmentId({
        spaceId,
        accessToken,
        assignmentId,
      });
      dispatch(fetchGradesSuccess({ ...payload, grades }));

      Analytics.track({
        event: FETCH_GRADES_FOR_ASSIGNMENT,
        totalGrades: grades[assignmentId].length,
      });
    } catch (error) {
      dispatch(fetchGradesError({ ...payload, error }));
    }
  };

export const updateAssignmentGrades =
  ({ assignmentId, studentId }) =>
  async (dispatch, getState) => {
    const payload = { assignmentId };

    dispatch(updateAssignmentGradesStarted(payload));

    const spaceId = coreSelectors.getCurrentSpaceId(getState());
    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);
    const allAssignmentIds = getAssignmentsIds(getState()).join(',');
    const isEducator = coreSelectors.getIsEducator(getState());

    try {
      const { grades } = await getGradesByAssignmentId({
        spaceId,
        accessToken,
        assignmentId,
        ...(isEducator ? {} : { studentId }),
      });
      const { grades: studentGrades } = await getGradesByAssignmentId({
        spaceId,
        accessToken,
        assignmentId: allAssignmentIds,
        studentId,
      });
      dispatch(updateAssignmentGradesSuccess({ ...payload, grades }));
      dispatch(
        fetchGradesForStudentSuccess({
          ...payload,
          grades: studentGrades,
          studentId,
        })
      );
    } catch (error) {
      dispatch(updateAssignmentGradesError({ ...payload, error }));
    }
  };

export const fetchGradesForAssignments =
  ({ assignmentIds }) =>
  async (dispatch, getState) => {
    const payload = { assignmentIds };

    dispatch(fetchGradesStarted(payload));

    const spaceId = coreSelectors.getCurrentSpaceId(getState());
    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);

    try {
      const { grades } = await getGradesByAssignmentId({
        spaceId,
        accessToken,
        assignmentId: assignmentIds.join(','),
      });

      dispatch(fetchGradesSuccess({ ...payload, grades }));
    } catch (error) {
      dispatch(fetchGradesError({ ...payload, error }));
    }
  };

export const fetchGradesForStudent =
  ({ studentId }) =>
  async (dispatch, getState) => {
    const payload = { studentId };

    dispatch(fetchGradesForStudentStarted(payload));

    const spaceId = coreSelectors.getCurrentSpaceId(getState());
    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);
    const assignmentId = getAssignmentsIds(getState()).join(',');

    try {
      const { grades } = await getGradesByAssignmentId({
        spaceId,
        accessToken,
        assignmentId,
        studentId,
      });
      dispatch(fetchGradesForStudentSuccess({ ...payload, grades }));
    } catch (error) {
      dispatch(fetchGradesForStudentError({ ...payload, error }));
    }
  };

export const createAssignment =
  ({ assignment: requestedAssignment }) =>
  async (dispatch, getState) => {
    dispatch(createAssignmentStarted({ requestedAssignment }));

    const spaceId = coreSelectors.getCurrentSpaceId(getState());
    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);

    try {
      const { assignment: createdAssignment } = await postAssignment({
        assignment: requestedAssignment,
        accessToken,
      });

      dispatch(
        createAssignmentSuccess({ requestedAssignment, createdAssignment })
      );

      Analytics.track({
        event: CREATE_ASSIGNMENT,
        assignment: createdAssignment,
      });
    } catch (error) {
      dispatch(createAssignmentError({ requestedAssignment, error }));
      throw error;
    }
  };

export const releaseGrades =
  ({ assignmentId }) =>
  async (dispatch, getState) => {
    dispatch(releaseGradesStarted({ assignmentId }));

    const spaceId = coreSelectors.getCurrentSpaceId(getState());
    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);

    try {
      const { gradesReleasedAt } = await postReleaseGrades({
        assignmentId,
        accessToken,
      });

      dispatch(releaseGradesSuccess({ assignmentId, gradesReleasedAt }));

      Analytics.track({
        event: RELEASE_GRADES,
        assignmentId,
        gradesReleasedAt,
      });
    } catch (error) {
      dispatch(releaseGradesError({ assignmentId, error }));
      throw error;
    }
  };

export const updateAssignment =
  ({ assignment: requestedAssignment }) =>
  async (dispatch, getState) => {
    dispatch(updateAssignmentStarted({ requestedAssignment }));

    const spaceId = coreSelectors.getCurrentSpaceId(getState());
    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);

    try {
      const { assignment: updatedAssignment } = await patchAssignment({
        assignmentId: requestedAssignment.id,
        updates: {
          title: requestedAssignment.title,
          gradeType: requestedAssignment.gradeType,
        },
        accessToken,
      });

      dispatch(
        updateAssignmentSuccess({ requestedAssignment, updatedAssignment })
      );

      Analytics.track({
        event: EDIT_ASSIGNMENT,
        assignment: updatedAssignment,
      });
    } catch (error) {
      dispatch(updateAssignmentError({ requestedAssignment, error }));
      throw error;
    }
  };

export const deleteAssignment =
  ({ assignmentId }) =>
  async (dispatch, getState) => {
    const spaceId = coreSelectors.getCurrentSpaceId(getState());
    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);

    try {
      await deleteAssignmentEffect({ assignmentId, accessToken });

      dispatch(deleteAssignmentSuccess({ assignmentId }));

      Analytics.track({
        event: DELETE_ASSIGNMENT,
        assignment: { id: assignmentId },
      });
    } catch (error) {
      dispatch(deleteAssignmentError({ assignmentId, error }));
      throw error;
    }
  };

export const gradeManualAssignment =
  ({ assignmentId, grade: rawGrade, studentId }) =>
  async (dispatch, getState) => {
    // send null if grade is empty
    // this will unset the grade
    const grade = hasGrade({ displayGrade: rawGrade }) ? rawGrade : null;

    dispatch(gradeManualAssignmentStarted({ assignmentId, studentId }));

    const spaceId = coreSelectors.getCurrentSpaceId(getState());
    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);

    try {
      await postGrade({
        assignmentId,
        grade,
        studentId,
        accessToken,
      });

      dispatch(
        gradeManualAssignmentSuccess({ assignmentId, grade, studentId })
      );

      Analytics.track({
        event: GRADE_MANUAL_ASSIGNMENT,
        assignmentId,
        grade,
        studentId,
      });
    } catch (error) {
      dispatch(
        gradeManualAssignmentError({
          assignmentId,
          studentId,
          error,
        })
      );
      throw error;
    }
  };

export const importManualAssignmentGrades =
  ({ assignmentId, grades }) =>
  async (dispatch, getState) => {
    dispatch(importManualAssignmentGradesStarted({ assignmentId, grades }));

    const spaceId = coreSelectors.getCurrentSpaceId(getState());
    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);

    try {
      await postGrades({
        assignmentId,
        grades,
        accessToken,
      });

      dispatch(importManualAssignmentGradesSuccess({ assignmentId, grades }));

      Analytics.track({
        event: GRADE_MANUAL_ASSIGNMENT,
        assignmentId,
        grades,
        csvUpload: true,
      });
    } catch (error) {
      dispatch(
        importManualAssignmentGradesError({ assignmentId, error, grades })
      );
      throw error;
    }
  };
